import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { validateCIN } from '../../../../../../utils/utils';
import { BASIC_DETAILS_LABELS } from './constants';
import styles from './BasicDetailsSidebar.module.scss';
import detailStyles from '../Details/Details.module.scss';

interface BasicDetailsSidebarProps {
    panNumber?: string;
    cinNumber?: string;
    gstin?: any[];
    headquarterLocation?: { fullAddress?: string };
    /**
     * Already formatted for display by the caller. Lives here rather than in the
     * Company Details grid because the main overview API returns foundationDate even
     * when the overview is LOCKED, and this card still renders in that state.
     */
    foundedDate?: string | null;
}

const BasicDetailsSidebar = ({ panNumber, cinNumber, gstin, headquarterLocation, foundedDate }: BasicDetailsSidebarProps) => {
    const hasData =
        foundedDate ||
        (panNumber && panNumber !== '') ||
        (cinNumber && cinNumber !== '') ||
        (gstin && gstin.length > 0) ||
        headquarterLocation?.fullAddress;

    if (!hasData) return null;

    const getGstInValue = (gstinObj: any) => {
        const [, gstinValue] = Object.entries(gstinObj)[0] as [string, string];
        return gstinValue;
    };

    const getToolTipContent = (gstinList: any[]) => (
        <div className={styles.GstTooltipList}>
            {gstinList?.map((gstinObj) => {
                const [stateName, gstinValue] = Object.entries(gstinObj)[0] as [string, string];
                return (
                    <div className={detailStyles.FormerEntityTooltip} key={`${stateName}-${gstinValue}`}>
                        <div className={detailStyles.FormerEntityName}>
                            <span className={styles.GstStateName}>{stateName}:</span> <span>{gstinValue}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className={styles.BasicDetailsSidebar}>
            <div className={styles.Header}>
                <div className={styles.Heading}>{BASIC_DETAILS_LABELS.HEADING}</div>
            </div>
            <div className={styles.Body}>
                {foundedDate && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>{BASIC_DETAILS_LABELS.FOUNDED}</span>
                        <span className={styles.Value}>{foundedDate}</span>
                    </div>
                )}
                {panNumber && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>{BASIC_DETAILS_LABELS.PAN}</span>
                        <span className={styles.Value}>{panNumber}</span>
                    </div>
                )}
                {cinNumber && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>
                            {validateCIN(cinNumber) ? BASIC_DETAILS_LABELS.CIN : BASIC_DETAILS_LABELS.LLPIN}
                        </span>
                        <span className={styles.Value}>{cinNumber}</span>
                    </div>
                )}
                {gstin && gstin.length > 0 && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>{BASIC_DETAILS_LABELS.GST}</span>
                        <div className={styles.GstValues}>
                            <span className={styles.Value}>{getGstInValue(gstin[0])}</span>
                            {gstin.length > 1 && <span className={styles.Value}>{getGstInValue(gstin[1])}</span>}
                            {gstin.length > 2 && (
                                <Tooltip
                                    content={getToolTipContent(gstin.slice(2))}
                                    direction="left"
                                    isOverviewDetail={true}
                                    usePortal={true}
                                    fitContent={true}
                                    hideDelay={500}
                                    style={{ width: 'max-content', minWidth: '18rem', maxWidth: '26.25rem' }}
                                    contentCss={{ maxHeight: '14rem', overflowY: 'auto' }}
                                >
                                    <span className={styles.MoreLink}>+{gstin.length - 2} more</span>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                )}
                {headquarterLocation?.fullAddress && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>{BASIC_DETAILS_LABELS.ADDRESS}</span>
                        <span className={styles.AddressValue}>{headquarterLocation.fullAddress}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BasicDetailsSidebar;
